'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { IndianRupee, ShoppingBag, Package, TrendingUp, Warehouse, RotateCcw, XCircle, Megaphone, AlertTriangle, Clock, BadgeDollarSign, BarChart2, Target, Zap } from 'lucide-react';
import { fetchDashboardMetrics } from '@/lib/supabase/dashboardService';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface KpiCardProps {
  label: string;
  value: string;
  change: number;
  changeLabel: string;
  icon: React.ElementType;
  variant?: 'default' | 'positive' | 'negative' | 'warning' | 'info';
  size?: 'hero' | 'md' | 'sm';
}

function KpiCard({ label, value, change, changeLabel, icon: Icon, variant = 'default', size = 'md' }: KpiCardProps) {
  const bgMap: Record<string, string> = {
    default: 'bg-card',
    positive: 'bg-positive-bg',
    negative: 'bg-negative-bg',
    warning: 'bg-warning-bg',
    info: 'bg-info-bg',
  };
  const changeColor = change > 0 ? 'text-positive' : change < 0 ? 'text-negative' : 'text-muted-foreground';
  return (
    <div className={`rounded-xl border border-border shadow-card card-hover p-4 flex flex-col gap-2 cursor-pointer ${bgMap[variant]}`}>
      <div className="flex items-center justify-between">
        <p className="text-2xs font-600 uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon size={15} className={variant === 'default' ? 'text-muted-foreground' : `text-${variant}`} />
      </div>
      <p className={`font-mono-nums font-700 text-foreground leading-none ${size === 'hero' ? 'text-3xl' : 'text-2xl'}`}>{value}</p>
      <div className={`flex items-center gap-1 text-xs font-500 ${changeColor}`}>
        {change !== 0 && <TrendingUp size={11} className={change < 0 ? 'rotate-180' : ''} />}
        <span>{change > 0 ? '+' : ''}{change}%</span>
        <span className="text-muted-foreground font-400">{changeLabel}</span>
      </div>
    </div>
  );
}

function SkeletonCard({ size = 'md' }: { size?: 'hero' | 'md' }) {
  return (
    <div className="rounded-xl border border-border shadow-card p-4 flex flex-col gap-2 bg-card animate-pulse">
      <div className="h-3 bg-muted rounded w-2/3" />
      <div className={`bg-muted rounded ${size === 'hero' ? 'h-9 w-3/4' : 'h-7 w-1/2'}`} />
      <div className="h-3 bg-muted rounded w-1/3" />
    </div>
  );
}

export default function MetricsBentoGrid() {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadMetrics = useCallback(() => {
    fetchDashboardMetrics().then((data) => {
      setMetrics(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadMetrics();

    const supabase = createClient();
    const channel = supabase
      .channel('metrics-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, loadMetrics)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'inventory_snapshots' }, loadMetrics)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ad_campaigns' }, loadMetrics)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadMetrics]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-3">
        {Array.from({ length: 15 }).map((_, i) => <SkeletonCard key={`sk-${i}`} size={i === 0 ? 'hero' : 'md'} />)}
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        Unable to load metrics. Please check your database connection.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-3">
      <KpiCard label="Gross Sales" value={metrics.grossSales.value} change={metrics.grossSales.change} changeLabel={metrics.grossSales.label} icon={IndianRupee} variant="info" size="hero" />
      <KpiCard label="Net Sales" value={metrics.netSales.value} change={metrics.netSales.change} changeLabel={metrics.netSales.label} icon={BadgeDollarSign} variant="positive" />
      <KpiCard label="Units Sold" value={metrics.unitsSold.value} change={metrics.unitsSold.change} changeLabel={metrics.unitsSold.label} icon={ShoppingBag} />
      <KpiCard label="Orders Received" value={metrics.ordersReceived.value} change={metrics.ordersReceived.change} changeLabel={metrics.ordersReceived.label} icon={Package} />
      <KpiCard label="Avg Selling Price" value={metrics.avgSellingPrice.value} change={metrics.avgSellingPrice.change} changeLabel={metrics.avgSellingPrice.label} icon={Target} variant={metrics.avgSellingPrice.change < 0 ? 'warning' : 'default'} />
      <KpiCard label="Total Available Stock" value={metrics.totalStock.value} change={metrics.totalStock.change} changeLabel={metrics.totalStock.label} icon={Warehouse} variant={metrics.totalStock.change < 0 ? 'warning' : 'default'} />
      <KpiCard label="Days of Inventory" value={metrics.daysOfInventory.value} change={metrics.daysOfInventory.change} changeLabel={metrics.daysOfInventory.label} icon={Clock} variant={metrics.daysOfInventory.raw < 15 ? 'warning' : 'default'} />
      <KpiCard label="Returns Value" value={metrics.returns.value} change={metrics.returns.change} changeLabel={metrics.returns.label} icon={RotateCcw} variant="negative" />
      <KpiCard label="Cancellation Rate" value={metrics.cancellationRate.value} change={metrics.cancellationRate.change} changeLabel={metrics.cancellationRate.label} icon={XCircle} variant={metrics.cancellationRate.raw > 3 ? 'negative' : 'warning'} />
      <KpiCard label="Ad Spend" value={metrics.adSpend.value} change={metrics.adSpend.change} changeLabel={metrics.adSpend.label} icon={Megaphone} variant="warning" />
      <KpiCard label="Attributed Ad Sales" value={metrics.adSales.value} change={metrics.adSales.change} changeLabel={metrics.adSales.label} icon={BarChart2} variant="positive" />
      <KpiCard label="ROAS" value={metrics.roas.value} change={metrics.roas.change} changeLabel={metrics.roas.label} icon={Zap} variant={metrics.roas.raw < 2 ? 'negative' : metrics.roas.raw < 3 ? 'warning' : 'positive'} />
      <KpiCard label="Est. Gross Profit" value={metrics.grossProfit.value} change={metrics.grossProfit.change} changeLabel={metrics.grossProfit.label} icon={TrendingUp} variant="positive" />
      <KpiCard label="Pending Settlements" value={metrics.pendingSettlements.value} change={0} changeLabel={metrics.pendingSettlements.label} icon={BadgeDollarSign} variant="info" />
      <KpiCard label="Stock-out SKUs" value={metrics.stockoutSkus.value} change={metrics.stockoutSkus.change} changeLabel={metrics.stockoutSkus.label} icon={AlertTriangle} variant="negative" />
    </div>
  );
}